// Фискалната фактура при закриване — разширеният фискален бон от
// фискалното устройство, с номер от ОБЩАТА поредица на обекта
// (docs/invoicing.md §Фискална фактура и обща поредица). Тук са чистите
// правила на FE; BE е източникът на истината и отказва всичко, което FE пропусне.

import type { TFunction } from "i18next";
import type { BillOut, FiscalDocumentFields } from "../api/bills";
import type { Company, InvoiceIssuingMode } from "../api/companies";
import type { FiscalInvoiceAvailability, Invoice } from "../api/invoices";
import type { ActivePaymentMethod } from "../api/paymentMethods";
import { parseMoneyToCents } from "../utils/money";
import { getApiErrorMessage } from "../utils/translations";

/** „По сметка" — без касов бон (`total = 0`), значи и без фискална фактура. */
const ON_ACCOUNT_PAYMENT_METHOD_ID = 4;
const CARD_PAYMENT_METHOD_ID = 2;
const MIXED_PAYMENT_METHOD_ID = 3;

/** „Къде се издават фактурите“ на фирмата; без поле (по-старо BE) — `software`,
 *  тоест както преди фискалната фактура. */
export const invoiceIssuingOf = (
  company: Pick<Company, "invoice_issuing"> | null | undefined,
): InvoiceIssuingMode => company?.invoice_issuing ?? "software";

/** `fiscal` и `both`: номерата на ВСИЧКИ фактури дава облакът (една поредица
 *  без дупки) — FE не номерира и не пише „Последен номер на фактура“. */
export const usesSharedInvoiceSeries = (mode: InvoiceIssuingMode): boolean =>
  mode !== "software";

/** Софтуерна фактура (по сметки, свободна, за депозит) — навсякъде освен в
 *  `fiscal`, където BE отказва всяка (409). */
export const softwareInvoicesAllowedIn = (mode: InvoiceIssuingMode): boolean =>
  mode !== "fiscal";

/** Защо текущата сметка няма фискална фактура при закриване. */
export type FiscalInvoiceBillBlock = "noBill" | "billClosed" | "closing" | "zeroBill" | "billDiscount";

/**
 * „Фактуриране" → „Текуща сметка": фискалната фактура е за избраната маса —
 * отворена, не се закрива в момента на този терминал и над 0.00 (бон за 0.00
 * няма). Причината или null (може). Същите правила като „Използвай депозит",
 * плюс отстъпката в € на цялата сметка — BE отказва фискална фактура за такава
 * (`fiscal_invoice_plan.py`).
 */
export const fiscalInvoiceBillBlockOf = (
  bill: Pick<BillOut, "status" | "total_amount" | "bill_discount_amount"> | null | undefined,
  isClosing: boolean,
): FiscalInvoiceBillBlock | null => {
  if (!bill) return "noBill";
  if (bill.status !== "open") return "billClosed";
  if (isClosing) return "closing";
  if ((parseMoneyToCents(bill.total_amount) ?? 0) <= 0) return "zeroBill";
  if ((parseMoneyToCents(String(bill.bill_discount_amount ?? 0)) ?? 0) > 0) return "billDiscount";
  return null;
};

/**
 * Методите в „Закрий" при фискална фактура: само с истински слот на
 * фискалния принтер (`print_route === "fiscal"`) — без „По сметка" и без
 * „БЕЗ ФИСКАЛИЗИРАНЕ". „Смесено" (без собствен път — синтезира се) остава,
 * ако не е изрично без бон; модалът го показва само при В брой И С карта.
 * `cardTerminal` (касата кара картов терминал — `fiscalInvoiceCardTerminalOf`):
 * без „С карта" и без „Смесено" — картата би минала през терминала, а там
 * фискална фактура BE още отказва. Бакшишът е с метода на сметката, значи и
 * бакшиш с карта отпада.
 */
export const fiscalInvoiceMethodsOf = <Method extends Pick<ActivePaymentMethod, "id" | "print_route">>(
  methods: readonly Method[],
  { cardTerminal = false }: { cardTerminal?: boolean } = {},
): Method[] =>
  methods.filter((method) => {
    if (method.id === ON_ACCOUNT_PAYMENT_METHOD_ID) return false;
    if (cardTerminal && (method.id === CARD_PAYMENT_METHOD_ID || method.id === MIXED_PAYMENT_METHOD_ID)) {
      return false;
    }
    if (method.id === MIXED_PAYMENT_METHOD_ID) {
      return method.print_route !== "pos" && method.print_route !== "none";
    }
    return method.print_route === "fiscal";
  });

/** Касата кара картов терминал (`card_terminal` на `fiscal-availability`) —
 *  само изрично `true`; липсващо поле (по-старо BE) или още неизтеглено
 *  питане — false, тоест както преди: отказът идва от BE при „Закрий". */
export const fiscalInvoiceCardTerminalOf = (
  availability: Pick<FiscalInvoiceAvailability, "card_terminal"> | null | undefined,
): boolean => availability?.card_terminal === true;

/**
 * Документът е издаден от фискалното устройство (фискална фактура или
 * кредитно известие към нея): оригиналът е бонът, затова тук не се печата,
 * не се анулира и получателят не се редактира (BE отказва с 409). Кредитното
 * известие към СОФТУЕРНА фактура (`source: "software"`) не е такова — то се
 * печата (A4 и POS) като всяка фактура, но също не се анулира и не се редактира
 * (`isCreditNote`).
 */
export const isFiscalInvoiceDocument = (
  invoice: Pick<Invoice, "source" | "invoice_type">,
): boolean => invoice.source === "fiscal";

export const isCreditNote = (invoice: Pick<Invoice, "invoice_type">): boolean =>
  invoice.invoice_type === "credit_note";

/** Готов текст `{bg, en}` от BE — на езика на екрана, иначе `fallback`. */
export const localizedTextOf = (
  text: { bg: string; en: string } | null | undefined,
  fallback: string,
): string =>
  text ? getApiErrorMessage({ response: { data: { detail: text } } }, fallback) : fallback;

/**
 * Документът от ФУ в отговор с отпечатан бон/сторно. `recorded` — записан
 * във „Фактури": текстът с номерата е за toast-а. `recordFailed` — Е на
 * хартия, но записът не е станал (или е с уговорка): готовият текст на BE,
 * кехлибарено и постоянно — операторът не бива да го издава наново. `null` —
 * в отговора няма такъв документ и изходът е обичайният.
 */
export type FiscalDocumentOutcome =
  | { kind: "recorded"; text: string }
  | { kind: "recordFailed"; text: string };

/** Фискалната фактура от `/close` и от повторното издаване (`/close/retry`
 *  я печата от записания payload, без поле в тялото). */
export const fiscalInvoiceOutcomeOf = (
  result: FiscalDocumentFields | undefined,
  table: string,
  t: TFunction,
): FiscalDocumentOutcome | null => {
  if (result?.fiscal_invoice_error) {
    return {
      kind: "recordFailed",
      text: localizedTextOf(result.fiscal_invoice_error, t("fiscalInvoice.close.recordFailed")),
    };
  }
  const invoice = result?.fiscal_invoice;
  if (!invoice) return null;
  return {
    kind: "recorded",
    text: invoice.receipt_number
      ? t("fiscalInvoice.close.success", {
          table,
          invoice: invoice.invoice_number,
          receipt: invoice.receipt_number,
        })
      : t("fiscalInvoice.close.successNoReceipt", { table, invoice: invoice.invoice_number }),
  };
};

/** Кредитното известие от „Сторнирай всичко", „Възстанови" и `/storno/retry`. */
export const creditNoteOutcomeOf = (
  result: FiscalDocumentFields | undefined,
  t: TFunction,
): FiscalDocumentOutcome | null => {
  if (result?.credit_note_error) {
    return {
      kind: "recordFailed",
      text: localizedTextOf(result.credit_note_error, t("fiscalInvoice.storno.recordFailed")),
    };
  }
  const creditNote = result?.credit_note;
  if (!creditNote) return null;
  return {
    kind: "recorded",
    text: creditNote.receipt_number
      ? t("fiscalInvoice.storno.issued", {
          number: creditNote.invoice_number,
          receipt: creditNote.receipt_number,
        })
      : t("fiscalInvoice.storno.issuedNoReceipt", { number: creditNote.invoice_number }),
  };
};

/** Отговорът носи документ от ФУ (записан или не) — „Фактури" са се сменили. */
export const hasFiscalDocument = (result: FiscalDocumentFields | undefined): boolean =>
  !!(
    result?.fiscal_invoice ||
    result?.fiscal_invoice_error ||
    result?.credit_note ||
    result?.credit_note_error
  );
